import { jsx as _jsx, Fragment as _Fragment } from "preact/jsx-runtime";
import SessionSelector from '@genericBookings/components/SessionSelector';
import { useCallback, useContext, useEffect, } from 'preact/compat';
import { ThemeContext } from 'styled-components';
import { useFormContext, } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '@genericBookings/store/hooks';
import { getActivities, getFetchingStatus, getSelectedActivities, setActiveSessionForActivity, } from '@genericBookings/store/reducers/activities';
import { useStepCompletion } from '@widgets/DayTickets/context/Steps';
import useQueryParams from '@widgets/DIYDayTickets/hooks/useQueryParams';
import { useFieldsWrapper } from '@genericBookings/context/FieldsWrapper';
import NoSessions from '@genericBookings/components/SessionSelector/components/NoSessions';
import Time from '@widgets/shared/components/icons/Time';
import FormStep from '@genericBookings/components/FormStep';
import { useTimeConflict } from '@bookingForm/context/TimeConflicts';
import { useMessage } from '@genericBookings/context/Message';
function SessionSelectorWrapper({ options, productId, }) {
    const { showPriceOnBands, noSessionsText, isMulti, isBuilder, allowOverlaps, } = useContext(options);
    const { layout, stepIconColour, } = useContext(ThemeContext);
    const activitySelector = isBuilder ? getSelectedActivities : getActivities;
    const activities = useAppSelector(activitySelector);
    const dispatch = useAppDispatch();
    const { setValue, getValues, } = useFormContext();
    const { modifyStep, sessionStep, } = useStepCompletion();
    const { requestingSessions, fetchedSessions, requestingData, } = useAppSelector(getFetchingStatus);
    const { reservationFailed } = useFieldsWrapper();
    const { sessions: paramSessions, sessionId: paramSessionId, } = useQueryParams(options);
    const { convertActivityToSession, initTimePeriods, } = useTimeConflict();
    const generateSession = useCallback((activity, session) => {
        if (activity.activity.durationInMinutes && session) {
            return convertActivityToSession({
                ...activity,
                activeSession: session,
            });
        }
        return null;
    }, [convertActivityToSession]);
    const findSessionByIDForActivity = useCallback((activity, id) => {
        if (!id)
            return undefined;
        return activity.sessions.find((curr) => curr.SessionId === id);
    }, []);
    const { addMessage, } = useMessage();
    const addMessagesForSession = useCallback((activity, session) => {
        if (session && session.Warning) {
            addMessage({
                type: 'warning',
                text: session.Warning ?? '',
                closable: false,
                sessionId: session.SessionId,
                activityId: activity.activity.id,
            });
        }
        if (session && session.Error) {
            addMessage({
                type: 'error',
                text: session.Error ?? '',
                closable: false,
                sessionId: session.SessionId,
                activityId: activity.activity.id,
            });
        }
    }, [addMessage]);
    // TODO: on date change reset the fetched sessions status
    const handleDidMount = useCallback(async () => {
        const sessions = [];
        if (!isMulti && paramSessionId) {
            setValue('session', paramSessionId);
            if (activities.length > 0) {
                const session = findSessionByIDForActivity(activities[0], paramSessionId);
                addMessagesForSession(activities[0], session);
                dispatch(setActiveSessionForActivity({
                    currActivity: activities[0],
                    session,
                }));
            }
        }
        if (isMulti && paramSessions && paramSessions.length > 0) {
            if (!isBuilder) {
                setValue('Sessions', paramSessions);
            }
            // get the param session for each activity...
            activities.forEach((curr) => {
                const paramSession = paramSessions.find((param) => curr.activity.id === param.ActivityId);
                const session = findSessionByIDForActivity(curr, (paramSession && paramSession.SessionId) ? paramSession.SessionId : undefined);
                addMessagesForSession(curr, session);
                dispatch(setActiveSessionForActivity({
                    currActivity: curr,
                    session,
                }));
                const sessionData = generateSession(curr, session);
                if (sessionData) {
                    sessions.push(sessionData);
                }
            });
        }
        if (!allowOverlaps && sessions.length > 0) {
            initTimePeriods(sessions);
        }
        if ((paramSessionId || paramSessions) && sessionStep) {
        }
        modifyStep(sessionStep, false, true, true);
    }, [
        isMulti,
        paramSessionId,
        paramSessions,
        allowOverlaps,
        sessionStep,
        modifyStep,
        setValue,
        activities,
        findSessionByIDForActivity,
        addMessagesForSession,
        dispatch,
        isBuilder,
        generateSession,
        initTimePeriods,
    ]);
    useEffect(() => {
        if (activities.length > 0 && sessionStep && !sessionStep.isViewed) {
            handleDidMount()
                .then(() => {
            });
        }
    }, [activities, fetchedSessions, handleDidMount, sessionStep]);
    const getSessionFieldName = useCallback((theActivity, idx) => {
        if (!isMulti) {
            return 'session';
        }
        const sessionValues = getValues('Sessions');
        return `Sessions.${theActivity && sessionValues
            ? sessionValues.findIndex((theSession) => theSession.ActivityId === theActivity.activity.id)
            : idx}.SessionId`;
    }, [getValues, isMulti]);
    const renderSessionSelector = useCallback((activity, idx) => {
        const selector = (_jsx(SessionSelector, { showPriceOnBands: showPriceOnBands ?? true, name: getSessionFieldName(activity, idx), restyled: layout === 'rework', options: options, activity: activity, isRequestingData: requestingSessions, step: sessionStep, productId: productId, listView: layout === 'winter-wonderland' }));
        if (isMulti) {
            return (_jsx(FormStep, { name: "session", label: activity.activity.label, sublabel: activity.activity.sublabel, labelIcon: _jsx(Time, { fill: stepIconColour ?? '#fff' }), selectedOptionTitle: "Choose Time", half: false, step: sessionStep, hideLabelOnEntry: false, showLabels: true, layout: 'rework', isWide: false, children: selector }));
        }
        return selector;
    }, [
        getSessionFieldName,
        isMulti,
        layout,
        options,
        productId,
        requestingSessions,
        showPriceOnBands,
        sessionStep,
        stepIconColour,
    ]);
    if (reservationFailed) {
        return (_jsx(NoSessions, { options: options, noSessionsText: noSessionsText ?? 'No Sessions Found' }));
    }
    if (requestingData) {
        return (_jsx("div", { style: {
                height: `${Math.min(activities.length * 144, 250)}px`,
                width: '100%',
            } }));
    }
    return (_jsx(_Fragment, { children: activities.map((curr, idx) => renderSessionSelector(curr, idx)) }));
}
export default SessionSelectorWrapper;
